import { Alert, Form, Input, InputNumber, Select } from "antd";
import type { CustomFieldProps } from "../../../types/core";
import type { IUnitPaymentPlan, PaymentType } from "../../../types/models/economics";
import { formatPart, percentageShare, planProblem } from "./planTerms";

/*
 * Every field of the plan is a custom one, not just the ones that need to be.
 * RecordFieldsMapper lifts custom fields above the generated ones, so a form
 * mixing the two would open on the payments and end on the name; with all of
 * them custom the form keeps the serializer's order.
 */

export const PlanNameField = (props: CustomFieldProps<IUnitPaymentPlan>) => (
  <Form.Item name={props.formName} label={props.fieldSchema.label}
             rules={[{ required: true, message: 'A plan needs a name, e.g. "Preventa".' }]}>
    <Input maxLength={100} />
  </Form.Item>
);

const UNIT_OPTIONS = [
  { value: 'fixed', label: "$" },
  { value: 'percentage', label: '%' },
];

/**
 * A payment's amount with its own unit beside it, because one plan can say
 * "30% de enganche" and "4 mensualidades de €10,000". The unit is the payment's
 * `<field>_type`, rendered as the input's addon; the type field itself renders
 * nothing (`PaymentTypeSlot`) so it is not drawn twice.
 *
 * The delivery payment can also be the remaining balance, which has no amount:
 * picking it clears and locks the input.
 */
const paymentField = (typeName: string, { remainder = false, extra }: { remainder?: boolean, extra?: (type: PaymentType) => string | undefined } = {}) =>
  (props: CustomFieldProps<IUnitPaymentPlan>) => (
    <Form.Item noStyle shouldUpdate={(prev, next) => prev[typeName] !== next[typeName]}>
      {({ getFieldValue, setFieldsValue }) => {
        const type: PaymentType = getFieldValue(typeName) || 'fixed';
        const isRemainder = type === 'remainder';
        return (
          <Form.Item name={props.formName} label={props.fieldSchema.label} extra={extra?.(type)}>
            <InputNumber
              min={0}
              max={type === 'percentage' ? 100 : undefined}
              disabled={isRemainder}
              placeholder={isRemainder ? 'Whatever the other payments leave' : undefined}
              style={{ width: '100%' }}
              addonAfter={
                <Form.Item name={typeName} noStyle>
                  <Select
                    aria-label={`${props.fieldSchema.label} unit`}
                    style={{ width: remainder ? 120 : 60 }}
                    options={remainder ? [...UNIT_OPTIONS, { value: 'remainder', label: 'Remaining' }] : UNIT_OPTIONS}
                    onChange={(next: PaymentType) => {
                      if (next === 'remainder') setFieldsValue({ [props.formName]: null });
                    }}
                  />
                </Form.Item>
              }
            />
          </Form.Item>
        );
      }}
    </Form.Item>
  );

/** A percentage off the price or a fixed amount off it, typed like a payment. */
export const DiscountField = paymentField('discount_type');
export const DownPaymentField = paymentField('down_payment_type');
export const InstallmentField = paymentField('installment_type', {
  extra: type => type === 'percentage' ? 'The share paid each month, not the total.' : undefined,
});
export const DeliveryPaymentField = paymentField('delivery_payment_type', {
  remainder: true,
  extra: type => type === 'remainder' ? 'Printed as "Remanente como finiquito".' : undefined,
});

/** A `<payment>_type` field: drawn as its payment's addon, so nothing here. */
export const PaymentTypeSlot = () => null;

export const InstallmentCountField = (props: CustomFieldProps<IUnitPaymentPlan>) => (
  <Form.Item name={props.formName} label="Number of installments">
    <InputNumber min={0} precision={0} addonAfter="months" style={{ width: '100%' }} />
  </Form.Item>
);

export const PlanOrderField = (props: CustomFieldProps<IUnitPaymentPlan>) => (
  <Form.Item name={props.formName} label="Order"
             extra="Plans are shown side by side in this order, lowest first.">
    <InputNumber precision={0} style={{ width: '100%' }} />
  </Form.Item>
);

/** The parent unit, set from the table's filter rather than chosen here. */
export const HiddenUnitField = (props: CustomFieldProps<IUnitPaymentPlan>) => (
  <Form.Item name={props.formName} hidden>
    <InputNumber />
  </Form.Item>
);

/**
 * What the plan's percentages add up to, live. A plan the server would refuse
 * says so before Save. Fixed amounts are quoted against a price that moves, so
 * they are never checked against it.
 */
export const PlanTotalCheck = () => (
  <Form.Item noStyle shouldUpdate>
    {({ getFieldsValue }) => {
      const plan = getFieldsValue(true) as IUnitPaymentPlan;
      const problem = planProblem(plan);
      if (problem) {
        return <Alert type="warning" showIcon message={problem} />;
      }
      const share = percentageShare(plan);
      if (!share) {
        return null;
      }
      const rest = plan.delivery_payment_type === 'remainder'
        ? ', and the remaining balance is the rest' : '';
      return <Alert type="success" showIcon message={`Percentages: ${formatPart(share, 'percentage')} of the price${rest}.`} />;
    }}
  </Form.Item>
);
